<!--
// app/components/pages/region-matches-match-homeTeam-vs-awayTeam/PlayerStatsModal.vue
-->
<script setup lang="ts">
import type { Player } from "#shared/player/domain/Player";
import type { Team } from "#shared/team/domain/models/Team";
import type { SeasonStatsPartner } from "#shared/season-cluster/domain/models/01.Season";
import type { Image } from "#shared/Shared/Types";
import type { MatchStatsPlayerData } from "#shared/stats/domain/models/MatchStats";
import CardDraftPlayer from "~/components/Shared/players/CardDraftPlayer.vue";
import CardPlayerFeaturedCategory from "~/components/Shared/players/CardPlayerFeaturedCategory.vue";

type Props = {
  player: Player;
  playerImage?: Image | undefined;
  team: Team | undefined;
  playerStatsData: MatchStatsPlayerData[];
  seasonId: number | undefined;
  mvpPartner?: SeasonStatsPartner;
};

defineEmits(["close"]);

const props = defineProps<Props>();
</script>

<template>
  <div class="bg-gray-900 fixed bottom-0 top-0 left-0 right-0 bg-opacity-40 z-50" @click="$emit('close')">
    <div
      @click.stop
      class="py-4 rounded-lg bg-black border-2 border-gray-800 fixed bottom-0 top-0 left-0 right-0 w-full h-fit m-auto max-w-2xl max-h-[650px] min-w-[320px]"
    >
      <div class="flex justify-end mb-2 px-4">
        <button
          type="button"
          class="text-gray-400 bg-gray-800 hover:bg-primary hover:text-black rounded-lg text-sm w-6 h-6 ms-auto inline-flex justify-center items-center"
          @click="$emit('close')"
        >
          <svg
            class="w-2 h-2"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 14 14"
          >
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Close modal</span>
        </button>
      </div>

      <div class="px-4">
        <card-draft-player
          v-if="player && (player.category === 'draft' || !player.category)"
          :key="player.id"
          :player="player"
          :player-team="team"
          :player-image="playerImage"
          :player-complete-name="player.shortName"
          :player-number="player.jersey || player.jersey === 0 ? player.jersey.toString() : undefined"
          :player-position="player.role"
          :season-id="seasonId"
          :player-id="player.id"
          :mvp-partner="mvpPartner"
          :show-clause="!!seasonId && seasonId !== 28"
          no-link
        ></card-draft-player>
        <card-player-featured-category
          v-if="player && player.category && player.category !== 'draft'"
          :key="player.id"
          :player-number="player.jersey || player.jersey === 0 ? player.jersey.toString() : undefined"
          :season-id="seasonId"
          :player-category="player.category"
          :player-complete-name="player.shortName"
          :player-position="player.role"
          :player-team="team"
          :player-image="playerImage"
          :player="player"
          :player-id="player.id"
          :mvp-partner="mvpPartner"
          show-category
          no-link
        ></card-player-featured-category>
      </div>
      <div class="w-full" v-if="playerStatsData?.length > 0">
        <p class="px-4 my-4 text-primary text-xs text-center uppercase">
          {{ $t("player.stats.modal.match.stats") }}
        </p>
        <ul class="overflow-auto lg:max-h-80 max-h-60 px-4">
          <li v-for="playerStat in playerStatsData">
            <div class="flex justify-between mb-1 rounded-lg px-5 py-2 bg-gray-800">
              <p class="text-gray-300 text-base">{{ playerStat?.parameterName }}</p>
              <p class="text-base font-bold">{{ playerStat?.total }}</p>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* width */
::-webkit-scrollbar {
  @apply w-0.5;
}

/* Track */
::-webkit-scrollbar-track {
  @apply bg-black;
}

/* Handle */
::-webkit-scrollbar-thumb {
  @apply bg-gray-600 rounded-xl;
}

/* Handle on hover */
::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
